import { useAuth0 } from 'auth0/reactAuth0Spa';
import { checkLoginForError, dateFormatToDBFormat, formatBearerToken } from 'Common.functions';
import { DepartmentContext } from 'DepartmentWrapper';
import React, { useContext, useEffect, useState } from 'react';
import { fetchDataAgnostic, putData } from 'Services/dataApi';
import style from './DirectApplicationSettings.style';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import { DatePicker } from 'react-widgets/cjs';
import { toast } from 'react-toastify';

import RezRateBanner from 'Body/RezRATE/Common/RezRateBanner/RezRateBanner';
import dayjs from 'dayjs';
import GenerateApplicationURLCode from 'Body/RezRATE/Body/DirectApplication/GenerateApplicationURLCode/GenerateApplicationURLCode';

const DirectApplicationSettings = ({ windowWidth }) => {
  const [isGettingSettings, setIsGettingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [baseURL, setBaseURL] = useState();
  const [editableStartDate, setEditableStartDate] = useState(null);
  const [editableEndDate, setEditableEndDate] = useState(null);
  const [activeSeason, setActiveSeason] = useState(null);

  const dContext = useContext(DepartmentContext);
  const { department, season } = dContext;
  const { getTokenSilently, loginWithRedirect } = useAuth0();

  const widthToSwitchToMobile = 700;

  useEffect(() => {
    const { protocol, hostname, port } = window.location;
    const newBaseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    setBaseURL(newBaseUrl);
  }, []);

  useEffect(() => {
    if (!department?.pk_Department || !season?.pk_Season) {
      return;
    }

    getSettings();
  }, [department?.pk_Department, season?.pk_Season]);

  const updateActiveSeason = (newSeasonList = []) => {
    const seasonId = dContext?.season?.pk_Season;
    if (!seasonId) {
      setActiveSeason(null);
      setEditableStartDate(null);
      setEditableEndDate(null);
      return;
    }

    const newActiveSeason = newSeasonList.find((item) => item.pk_Season === seasonId);
    setActiveSeason(newActiveSeason || null);

    setEditableStartDate(newActiveSeason?.ApplicationStartDate ? new Date(newActiveSeason.ApplicationStartDate) : null);
    setEditableEndDate(newActiveSeason?.ApplicationEndDate ? new Date(newActiveSeason.ApplicationEndDate) : null);
  };

  const getSettings = () => {
    setIsGettingSettings(true);

    getTokenSilently()
      .then((token) => {
        fetchDataAgnostic('department', { pk_Department: department.pk_Department }, formatBearerToken(token))
          .then((res) => {
            const { data = {} } = res;
            const { season: seasonList = [] } = data;
            updateActiveSeason(seasonList);
          })
          .catch(() => {
            toast.error('Failed to load direct application season settings');
          })
          .finally(() => {
            setIsGettingSettings(false);
          });
      })
      .catch((err) => {
        setIsGettingSettings(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  const saveSeason = () => {
    if (!editableStartDate || !editableEndDate) {
      return;
    }

    setIsSaving(true);

    getTokenSilently()
      .then((token) => {
        putData(
          'department/season',
          { pk_Department: department.pk_Department, pk_Season: season.pk_Season },
          {
            seasonObject: {
              ApplicationStartDate: dateFormatToDBFormat(new Date(dayjs(editableStartDate).format())),
              ApplicationEndDate: dateFormatToDBFormat(new Date(dayjs(editableEndDate).format())),
            },
          },
          formatBearerToken(token),
        )
          .then(() => {
            toast.success('Season dates saved successfully');
            getSettings();
          })
          .catch((err) => {
            console.log('saveSeason err: ', err);
            toast.error('Error saving season dates');
          })
          .finally(() => {
            setIsSaving(false);
          });
      })
      .catch((err) => {
        setIsSaving(false);
        checkLoginForError({ err, loginWithRedirect });
      });
  };
  const hasDateChange = () => {
    const formattedEditableEndDate = editableEndDate ? dayjs(editableEndDate).format() : null;
    const formattedEditableStartDate = editableStartDate ? dayjs(editableStartDate).format() : null;

    const formattedStartDate = activeSeason ? dayjs(activeSeason.ApplicationStartDate).format() : null;
    const formattedEndDate = activeSeason ? dayjs(activeSeason.ApplicationEndDate).format() : null;

    if (activeSeason) {
      return (
        editableStartDate &&
        editableEndDate &&
        (formattedEditableEndDate !== formattedEndDate || formattedEditableStartDate !== formattedStartDate)
      );
    }

    return false;
  };
  return (
    <div style={style.simpleRow}>
      <div style={{ ...style.simpleColumn, width: '100%' }}>
        <GenerateApplicationURLCode
          windowWidth={windowWidth}
          widthToSwitchToMobile={widthToSwitchToMobile}
          baseURL={baseURL}
        />

        <br />

        <Card>
          <CardHeader>
            <div style={{ ...style.simpleRow, alignItems: 'center', fontWeight: 'bold' }}>
              Application Window for {activeSeason ? activeSeason.SeasonName : ''}
            </div>
          </CardHeader>
          <CardBody>
            {(() => {
              const isMobile = typeof window !== 'undefined' && window.innerWidth < widthToSwitchToMobile;

              return (
                <div
                  style={
                    isMobile ? { display: 'flex', flexDirection: 'column', gap: 10, width: '100%' } : { width: '100%' }
                  }
                >
                  <div style={{ width: '100%' }}>
                    <RezRateBanner
                      message={activeSeason && !activeSeason.Active ? 'This is an inactive season!' : null}
                      backgroundColor={activeSeason && !activeSeason.Active ? '#f0ad4e' : null}
                    />
                  </div>

                  <div
                    style={
                      isMobile
                        ? { width: '100%' }
                        : {
                            display: 'flex',
                            flexDirection: 'row',
                            paddingRight: '10%',
                            paddingLeft: '10%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 10,
                          }
                    }
                  >
                    <div
                      style={
                        isMobile
                          ? { width: '100%' }
                          : { ...style.simpleColumn, width: '50%', paddingLeft: 30, paddingRight: 30 }
                      }
                    >
                      <div style={style.simpleRow}>Start Accepting Applications on</div>
                      <div style={{ ...style.simpleColumn, width: '100%' }}>
                        <DatePicker
                          placeholder="Select a start date"
                          value={editableStartDate}
                          disabled={isGettingSettings || isSaving}
                          onChange={(event) => {
                            setEditableStartDate(event);
                          }}
                          valueFormat={{ month: 'short', year: 'numeric' }}
                          valueEditFormat={{ dateStyle: 'long' }}
                          valueDisplayFormat={{ dateStyle: 'long' }}
                        />
                      </div>
                    </div>

                    <div
                      style={
                        isMobile
                          ? { width: '100%' }
                          : { ...style.simpleColumn, width: '50%', paddingLeft: 30, paddingRight: 30 }
                      }
                    >
                      <div style={style.simpleRow}>End Accepting Applications on</div>
                      <div style={style.simpleRow}>
                        <div style={{ ...style.simpleColumn, width: '100%' }}>
                          <DatePicker
                            placeholder="Select an end date"
                            value={editableEndDate}
                            disabled={isGettingSettings || isSaving}
                            onChange={(event) => {
                              setEditableEndDate(event);
                            }}
                            valueFormat={{ month: 'short', year: 'numeric' }}
                            valueEditFormat={{ dateStyle: 'long' }}
                            valueDisplayFormat={{ dateStyle: 'long' }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Button
                      size="sm"
                      disabled={!hasDateChange() || isSaving}
                      style={{ width: 150 }}
                      color="success"
                      onClick={saveSeason}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              );
            })()}
          </CardBody>
        </Card>

        <br />
      </div>
    </div>
  );
};

export default DirectApplicationSettings;
